import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { FiCheckCircle, FiEdit2, FiPlus, FiSearch, FiTrash2, FiUserPlus, FiUserX } from "react-icons/fi";
import VmsPageCard from "../components/VmsPageCard";
import { VMS_COLORS } from "../constants/theme";
import { vmsApi } from "../services/vmsApi";
import { isVmsGeneralAdmin } from "../utils/auth";

const VmsPeopleUsers = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOptionsOpen,
    onOpen: onDeleteOptionsOpen,
    onClose: onDeleteOptionsClose,
  } = useDisclosure();
  const isAdmin = isVmsGeneralAdmin();
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const [offices, setOffices] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [searchFilter, setSearchFilter] = useState("");
  const [persons, setPersons] = useState([]);
  const [form, setForm] = useState({
    office_id: "",
    full_name: "",
    title: "",
    profile_image: "",
    email: "",
    phone_number: "",
    create_login: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState("");
  const [editProfileImageFile, setEditProfileImageFile] = useState(null);
  const [editProfilePreviewUrl, setEditProfilePreviewUrl] = useState("");
  const [editForm, setEditForm] = useState({
    person_id: "",
    office_id: "",
    full_name: "",
    title: "",
    profile_image: "",
    email: "",
    phone_number: "",
    status: "active",
  });
  const [deleteTargetPerson, setDeleteTargetPerson] = useState(null);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    message: "",
    actionType: null,
    person: null,
  });

  const loadOffices = async () => {
    try {
      const data = await vmsApi.getOffices();
      const list = Array.isArray(data) ? data : data?.data || [];
      setOffices(list);
      if (!selectedOffice && isAdmin) {
        setSelectedOffice("all");
      } else if (!selectedOffice && list[0]) {
        const firstId = String(list[0].id || list[0].office_id);
        setSelectedOffice(firstId);
      }
    } catch (err) {
      toast({ title: "Failed to load offices", description: err?.message, status: "error" });
    }
  };

  const loadPersons = async (officeId) => {
    if (!officeId) return;
    try {
      const response = await vmsApi.listPersons({
        office_id: officeId,
        status: statusFilter,
        search: searchFilter.trim(),
      });
      const items = Array.isArray(response?.persons) ? response.persons : [];
      setPersons(items);
    } catch (err) {
      toast({ title: "Failed to load persons", description: err?.message, status: "error" });
    }
  };

  useEffect(() => {
    loadOffices().catch(() => {});
  }, []);

  useEffect(() => {
    loadPersons(selectedOffice);
  }, [selectedOffice, statusFilter]);

  useEffect(() => {
    return () => {
      if (profilePreviewUrl) {
        URL.revokeObjectURL(profilePreviewUrl);
      }
      if (editProfilePreviewUrl) {
        URL.revokeObjectURL(editProfilePreviewUrl);
      }
    };
  }, [profilePreviewUrl, editProfilePreviewUrl]);

  const openCreateModal = () => {
    const defaultOfficeId =
      selectedOffice && selectedOffice !== "all"
        ? selectedOffice
        : (offices[0] && String(offices[0].id || offices[0].office_id)) || "";
    setForm({
      office_id: defaultOfficeId,
      full_name: "",
      title: "",
      profile_image: "",
      email: "",
      phone_number: "",
      create_login: false,
    });
    setProfileImageFile(null);
    setProfilePreviewUrl("");
    onOpen();
  };

  const uploadProfileImage = async (file) => {
    const cloudName = process.env.REACT_APP_CLOUD_NAME || "drgenfwdh";
    const presetKey = process.env.REACT_APP_PRESET_KEY || "emanagement_img_preset";
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", presetKey);

    const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
      method: "POST",
      body: formData,
    });

    const data = await response.json();
    if (!response.ok || !data?.secure_url) {
      throw new Error(data?.error?.message || "Image upload failed.");
    }

    return data.secure_url;
  };

  const createPerson = async (e) => {
    e.preventDefault();
    const targetOfficeId = form.office_id || (selectedOffice !== "all" ? selectedOffice : "");
    if (!targetOfficeId) {
      toast({
        title: "Select a specific office",
        description: "Choose one office before creating a person.",
        status: "warning",
      });
      return;
    }

    const trimmedName = form.full_name.trim();
    const trimmedRole = form.title.trim();
    const trimmedEmail = form.email.trim();
    const trimmedPhone = form.phone_number.trim();

    if (!trimmedName) {
      toast({ title: "Validation error", description: "Full name is required.", status: "warning" });
      return;
    }

    if (!trimmedRole) {
      toast({ title: "Validation error", description: "Role is required.", status: "warning" });
      return;
    }

    if (!trimmedEmail) {
      toast({ title: "Validation error", description: "Email is required.", status: "warning" });
      return;
    }

    if (!trimmedPhone) {
      toast({ title: "Validation error", description: "Phone number is required.", status: "warning" });
      return;
    }

    if (!/^\d{10,15}$/.test(trimmedPhone)) {
      toast({
        title: "Validation error",
        description: "Phone number must be 10 to 15 digits.",
        status: "warning",
      });
      return;
    }

    setIsLoading(true);
    try {
      let resolvedProfileImage = "";
      if (profileImageFile) {
        resolvedProfileImage = await uploadProfileImage(profileImageFile);
      }

      const payload = {
        full_name: trimmedName,
        title: trimmedRole,
        profile_image: resolvedProfileImage,
        email: trimmedEmail,
        phone_number: trimmedPhone,
      };

      if (form.create_login) {
        const userPayload = {
          email: form.email.trim(),
          name: trimmedName,
          office_id: targetOfficeId,
        };

        const [personResult, userResult] = await Promise.allSettled([
          vmsApi.createPerson(targetOfficeId, payload),
          vmsApi.createOfficeUser(userPayload),
        ]);

        if (personResult.status === "rejected" || userResult.status === "rejected") {
          const personError =
            personResult.status === "rejected" ? personResult.reason?.message || "Person create failed." : null;
          const userError =
            userResult.status === "rejected" ? userResult.reason?.message || "Office login create failed." : null;
          const combined = [personError, userError].filter(Boolean).join(" ");
          throw new Error(combined || "Create request failed.");
        }
      } else {
        await vmsApi.createPerson(targetOfficeId, payload);
      }

      setForm({
        office_id: targetOfficeId,
        full_name: "",
        title: "",
        profile_image: "",
        email: "",
        phone_number: "",
        create_login: false,
      });
      setProfileImageFile(null);
      setProfilePreviewUrl("");
      await loadPersons(selectedOffice);
      onClose();
      toast({
        title: form.create_login ? "Person and login created" : "Person created",
        status: "success",
      });
    } catch (err) {
      toast({ title: "Create failed", description: err?.message, status: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserStatusAction = async (person, action) => {
    const userId = person?.login_user?.user_id;
    if (!userId) return;
    setIsActionLoading(true);
    try {
      await vmsApi.updateOfficeUserStatus(userId, action);
      toast({
        title: `User ${action}d successfully`,
        status: "success",
      });
      await loadPersons(selectedOffice);
    } catch (err) {
      toast({
        title: "Action failed",
        description: err?.message || "Could not update user status.",
        status: "error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDeleteUser = async (person) => {
    const userId = person?.login_user?.user_id;
    if (!userId) return;
    setIsActionLoading(true);
    try {
      await vmsApi.deleteOfficeUser(userId);
      toast({
        title: "User deleted successfully",
        status: "success",
      });
      await loadPersons(selectedOffice);
    } catch (err) {
      toast({
        title: "Delete failed",
        description: err?.message || "Could not delete user.",
        status: "error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDeletePerson = async (person) => {
    const personId = person?.person_id || person?.id;
    if (!personId) return;
    setIsActionLoading(true);
    try {
      const res = await vmsApi.deletePerson(personId);
      toast({
        title: res?.message || "Office person deleted successfully",
        status: "success",
      });
      await loadPersons(selectedOffice);
    } catch (err) {
      toast({
        title: "Delete person failed",
        description: err?.message || "Could not delete office person.",
        status: "error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDeleteBoth = async (person) => {
    setIsActionLoading(true);
    try {
      const userId = person?.login_user?.user_id;
      const personId = person?.person_id || person?.id;
      const tasks = [];
      if (userId) tasks.push(vmsApi.deleteOfficeUser(userId));
      if (personId) tasks.push(vmsApi.deletePerson(personId));
      await Promise.all(tasks);
      toast({
        title: "Login and office person deleted",
        status: "success",
      });
      await loadPersons(selectedOffice);
    } catch (err) {
      toast({
        title: "Delete both failed",
        description: err?.message || "Could not delete login and person.",
        status: "error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const openDeleteOptions = (person) => {
    setDeleteTargetPerson(person);
    onDeleteOptionsOpen();
  };

  const handleCreateLogin = async (person) => {
    const officeId = person?.office?.office_id || person?.office_id || selectedOffice;
    const email = person?.email;
    const name = person?.full_name || person?.name;

    if (!officeId || officeId === "all" || !email || !name) {
      toast({
        title: "Missing data",
        description: "Person must have office, name, and email before creating login.",
        status: "warning",
      });
      return;
    }

    setIsActionLoading(true);
    try {
      await vmsApi.createOfficeUser({
        email,
        name,
        office_id: officeId,
      });
      toast({
        title: "Login created",
        description: "Credentials have been sent to user's email.",
        status: "success",
      });
      await loadPersons(selectedOffice);
    } catch (err) {
      toast({
        title: "Create login failed",
        description: err?.message || "Could not create login details.",
        status: "error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const openEditModal = (person) => {
    const officeId = String(person?.office?.office_id || person?.office_id || "");
    setEditForm({
      person_id: person?.person_id || person?.id || "",
      office_id: officeId,
      full_name: person?.full_name || person?.name || "",
      title: person?.title || "",
      profile_image: person?.profile_image || "",
      email: person?.email || "",
      phone_number: person?.phone_number || person?.phone || "",
      status: (person?.status || "active").toLowerCase() === "inactive" ? "inactive" : "active",
    });
    setEditProfileImageFile(null);
    setEditProfilePreviewUrl(person?.profile_image || "");
    onEditOpen();
  };

  const updatePerson = async (e) => {
    e.preventDefault();
    if (!editForm.person_id) return;

    const trimmedName = editForm.full_name.trim();
    const trimmedRole = editForm.title.trim();
    const trimmedEmail = editForm.email.trim();
    const trimmedPhone = editForm.phone_number.trim();

    if (!trimmedName) {
      toast({ title: "Validation error", description: "Full name cannot be empty.", status: "warning" });
      return;
    }

    if (!editForm.office_id) {
      toast({ title: "Validation error", description: "Office is required.", status: "warning" });
      return;
    }

    if (!["active", "inactive"].includes(editForm.status)) {
      toast({ title: "Validation error", description: "Status must be active or inactive.", status: "warning" });
      return;
    }

    setIsLoading(true);
    try {
      let resolvedProfileImage = editForm.profile_image || "";
      if (editProfileImageFile) {
        resolvedProfileImage = await uploadProfileImage(editProfileImageFile);
      }

      await vmsApi.updatePerson(editForm.person_id, {
        office_id: editForm.office_id,
        full_name: trimmedName,
        title: trimmedRole,
        profile_image: resolvedProfileImage,
        email: trimmedEmail,
        phone_number: trimmedPhone,
        status: editForm.status,
      });

      toast({ title: "Person updated", status: "success" });
      onEditClose();
      await loadPersons(selectedOffice);
    } catch (err) {
      toast({ title: "Update failed", description: err?.message, status: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const openConfirm = (actionType, person) => {
    const name = person?.full_name || person?.name || "this user";
    const config = {
      create_login: {
        title: "Create Login",
        message: `Create login details for ${name}?`,
      },
      activate: {
        title: "Activate User",
        message: `Activate login access for ${name}?`,
      },
      deactivate: {
        title: "Make User Inactive",
        message: `Make ${name} inactive? They will not be able to login.`,
      },
      delete: {
        title: "Delete User",
        message: `Delete ${name}'s login account permanently?`,
      },
    };

    setConfirmState({
      isOpen: true,
      title: config[actionType]?.title || "Confirm action",
      message: config[actionType]?.message || "Proceed with this action?",
      actionType,
      person,
    });
  };

  const closeConfirm = () => {
    setConfirmState({
      isOpen: false,
      title: "",
      message: "",
      actionType: null,
      person: null,
    });
  };

  const runConfirmedAction = async () => {
    const { actionType, person } = confirmState;
    if (!actionType || !person) return;
    closeConfirm();

    if (actionType === "create_login") {
      await handleCreateLogin(person);
      return;
    }
    if (actionType === "activate" || actionType === "deactivate") {
      await handleUserStatusAction(person, actionType);
      return;
    }
    if (actionType === "delete") {
      await handleDeleteUser(person);
    }
  };

  return (
    <VmsPageCard>
      <VStack align="stretch" spacing={5}>
        <Box>
          <Text fontSize="xl" fontWeight="800" color="gray.800">
            Office Users
          </Text>
          <Text color="gray.500" fontSize="sm">
            Create and manage office users across offices.
          </Text>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
          <Stat bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="12px" p={4}>
            <StatLabel>Total users</StatLabel>
            <StatNumber>{persons.length}</StatNumber>
          </Stat>
          <Stat bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="12px" p={4}>
            <StatLabel>Office scope</StatLabel>
            <Text fontWeight="700">{selectedOffice === "all" ? "All offices" : "Single office"}</Text>
          </Stat>
          <Stat bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="12px" p={4}>
            <StatLabel>Status</StatLabel>
            <Text fontWeight="700" textTransform="capitalize">
              {statusFilter}
            </Text>
          </Stat>
          <Stat bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="12px" p={4}>
            <StatLabel>Search</StatLabel>
            <Text fontWeight="700" noOfLines={1}>
              {searchFilter || "None"}
            </Text>
          </Stat>
        </SimpleGrid>

        <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="14px" p={4}>
          <HStack align="end" spacing={4} wrap="wrap">
            <WrapItem minW={{ base: "100%", md: "260px" }}>
              <FormControl>
                <FormLabel mb={1}>Office</FormLabel>
                <Select value={selectedOffice} onChange={(e) => setSelectedOffice(e.target.value)} bg="white">
                  {isAdmin ? <option value="all">All offices</option> : null}
                  {offices.map((office) => {
                    const id = office.id || office.office_id;
                    return (
                      <option key={id} value={id}>
                        {office.name || office.code || id}
                      </option>
                    );
                  })}
                </Select>
              </FormControl>
            </WrapItem>
            <WrapItem minW={{ base: "100%", md: "180px" }}>
              <FormControl>
                <FormLabel mb={1}>Status</FormLabel>
                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} bg="white">
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </Select>
              </FormControl>
            </WrapItem>
            <WrapItem minW={{ base: "100%", md: "280px" }}>
              <FormControl>
                <FormLabel mb={1}>Search</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FiSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search by name or role"
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    bg="white"
                  />
                </InputGroup>
              </FormControl>
            </WrapItem>
            <WrapItem>
              <Button variant="outline" onClick={() => loadPersons(selectedOffice)}>
                Apply Filters
              </Button>
            </WrapItem>
            <Box flex="1" />
            <WrapItem>
              <Button
                leftIcon={<FiPlus />}
                onClick={openCreateModal}
                bg={VMS_COLORS.primary}
                color="white"
                _hover={{ bg: VMS_COLORS.primaryDark, transform: "translateY(-1px)" }}
                _active={{ transform: "translateY(0)" }}
              >
                Create Office User
              </Button>
            </WrapItem>
          </HStack>
        </Box>

        <TableContainer border="1px solid" borderColor="gray.200" borderRadius="12px">
          <Table size="sm" variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Name</Th>
                <Th>Office</Th>
                <Th>Title</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Status</Th>
                <Th>Login</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {persons.map((person) => (
                <Tr key={person.id || person.person_id || person.email} _hover={{ bg: "gray.50" }}>
                  <Td fontWeight="600">{person.full_name || person.name || "-"}</Td>
                  <Td>{person?.office?.name || person._office_name || "-"}</Td>
                  <Td>{person.title || "-"}</Td>
                  <Td>{person.email || "-"}</Td>
                  <Td>{person.phone_number || person.phone || "-"}</Td>
                  <Td>
                    <Badge colorScheme={(person.status || "inactive") === "active" ? "green" : "gray"}>
                      {person.status || "-"}
                    </Badge>
                  </Td>
                  <Td>
                    {person.has_login_details ? (
                      <Badge colorScheme={(person?.login_user?.status || "").toLowerCase() === "active" ? "green" : "orange"}>
                        {(person?.login_user?.status || "linked").toLowerCase()}
                      </Badge>
                    ) : (
                      <Button
                        size="xs"
                        variant="outline"
                        leftIcon={<FiUserPlus />}
                        onClick={() => openConfirm("create_login", person)}
                        isLoading={isActionLoading}
                      >
                        Create Login
                      </Button>
                    )}
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Tooltip label="Edit person details">
                        <IconButton
                          size="xs"
                          variant="outline"
                          icon={<FiEdit2 />}
                          aria-label="Edit person"
                          onClick={() => openEditModal(person)}
                          isLoading={isActionLoading}
                        />
                      </Tooltip>
                    {person.has_login_details ? (
                      <>
                        {(person?.login_user?.status || "").toLowerCase() === "active" ? (
                          <Tooltip label="Make user inactive">
                            <IconButton
                              size="xs"
                              variant="outline"
                              icon={<FiUserX />}
                              aria-label="Make inactive"
                              onClick={() => openConfirm("deactivate", person)}
                              isLoading={isActionLoading}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip label="Activate user">
                            <IconButton
                              size="xs"
                              variant="outline"
                              icon={<FiCheckCircle />}
                              aria-label="Activate user"
                              onClick={() => openConfirm("activate", person)}
                              isLoading={isActionLoading}
                            />
                          </Tooltip>
                        )}
                        <Tooltip label="Delete options">
                          <IconButton
                            size="xs"
                            colorScheme="red"
                            variant="ghost"
                            icon={<FiTrash2 />}
                            aria-label="Delete options"
                            onClick={() => openDeleteOptions(person)}
                            isLoading={isActionLoading}
                          />
                        </Tooltip>
                      </>
                    ) : null}
                    </HStack>
                  </Td>
                </Tr>
              ))}
              {!persons.length ? (
                <Tr>
                  <Td colSpan={8}>
                    <Text py={10} textAlign="center" color="gray.500">
                      No office users found for this filter.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </TableContainer>

        <Modal isOpen={isOpen} onClose={onClose} size="lg">
          <ModalOverlay />
          <ModalContent borderRadius="16px">
            <ModalHeader>Add Person</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box as="form" id="create-person-form" onSubmit={createPerson}>
                <Box mb={4} p={3} borderRadius="12px" bg="green.50" border="1px solid" borderColor="green.100">
                  <Text fontSize="sm" color="green.800">
                    Enable login to create an office account and email temporary credentials to the user.
                  </Text>
                </Box>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Office</FormLabel>
                    <Select
                      isRequired
                      value={form.office_id}
                      onChange={(e) => setForm({ ...form, office_id: e.target.value })}
                    >
                      <option value="">Select office</option>
                      {offices.map((office) => {
                        const id = String(office.id || office.office_id);
                        return (
                          <option key={id} value={id}>
                            {office.name || office.code || id}
                          </option>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Full Name</FormLabel>
                    <Input
                      isRequired
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Role</FormLabel>
                    <Input
                      isRequired
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Upload Profile Image</FormLabel>
                    <HStack
                      p={2}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="12px"
                      justify="space-between"
                    >
                      <Text fontSize="sm" color="gray.600" noOfLines={1}>
                        {profileImageFile?.name || "No file selected"}
                      </Text>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Choose file
                      </Button>
                    </HStack>
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      display="none"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setProfileImageFile(file);
                        setProfilePreviewUrl(URL.createObjectURL(file));
                        setForm((prev) => ({ ...prev, profile_image: "" }));
                      }}
                    />
                    {profilePreviewUrl ? (
                      <Box mt={2}>
                        <img
                          src={profilePreviewUrl}
                          alt="Profile preview"
                          style={{
                            width: "56px",
                            height: "56px",
                            borderRadius: "8px",
                            objectFit: "cover",
                            border: "1px solid #E2E8F0",
                          }}
                        />
                      </Box>
                    ) : null}
                    <Text mt={1} fontSize="xs" color="gray.500">
                      Upload a clear image (JPG or PNG).
                    </Text>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Email</FormLabel>
                    <Input
                      isRequired
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Phone Number</FormLabel>
                    <Input
                      isRequired
                      type="tel"
                      value={form.phone_number}
                      onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                      placeholder="08030000000"
                    />
                  </FormControl>
                  <FormControl display="flex" alignItems="center" pt={{ base: 0, md: 7 }}>
                    <Switch
                      isChecked={form.create_login}
                      onChange={(e) => setForm((prev) => ({ ...prev, create_login: e.target.checked }))}
                      colorScheme="green"
                      mr={3}
                    />
                    <Text fontSize="sm">Create login details for this person</Text>
                  </FormControl>
                </SimpleGrid>
                <Divider my={4} />
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="create-person-form"
                bg={VMS_COLORS.primary}
                color="white"
                _hover={{ bg: VMS_COLORS.primaryDark }}
                isLoading={isLoading}
              >
                {form.create_login ? "Create Person + Login" : "Create Person"}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Modal isOpen={isDeleteOptionsOpen} onClose={onDeleteOptionsClose} isCentered>
          <ModalOverlay />
          <ModalContent borderRadius="14px">
            <ModalHeader>Delete Options</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text color="gray.700">
                Choose what to delete for {deleteTargetPerson?.full_name || deleteTargetPerson?.name || "this person"}.
              </Text>
            </ModalBody>
            <ModalFooter>
              <HStack spacing={2} wrap="wrap">
                {deleteTargetPerson?.has_login_details ? (
                  <Button
                    variant="outline"
                    onClick={async () => {
                      onDeleteOptionsClose();
                      await handleDeleteUser(deleteTargetPerson);
                    }}
                    isLoading={isActionLoading}
                  >
                    Delete Login Only
                  </Button>
                ) : null}
                <Button
                  colorScheme="orange"
                  variant="outline"
                  onClick={async () => {
                    onDeleteOptionsClose();
                    await handleDeletePerson(deleteTargetPerson);
                  }}
                  isLoading={isActionLoading}
                >
                  Delete Office Person Only
                </Button>
                {deleteTargetPerson?.has_login_details ? (
                  <Button
                    colorScheme="red"
                    onClick={async () => {
                      onDeleteOptionsClose();
                      await handleDeleteBoth(deleteTargetPerson);
                    }}
                    isLoading={isActionLoading}
                  >
                    Delete Both
                  </Button>
                ) : null}
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Modal isOpen={isEditOpen} onClose={onEditClose} size="lg">
          <ModalOverlay />
          <ModalContent borderRadius="16px">
            <ModalHeader>Edit Office Person</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box as="form" id="edit-person-form" onSubmit={updatePerson}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Office</FormLabel>
                    <Select
                      isRequired
                      value={editForm.office_id}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, office_id: e.target.value }))}
                    >
                      <option value="">Select office</option>
                      {offices.map((office) => {
                        const id = String(office.id || office.office_id);
                        return (
                          <option key={id} value={id}>
                            {office.name || office.code || id}
                          </option>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Full Name</FormLabel>
                    <Input
                      isRequired
                      value={editForm.full_name}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, full_name: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Role</FormLabel>
                    <Input
                      value={editForm.title}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Status</FormLabel>
                    <Select
                      isRequired
                      value={editForm.status}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                    >
                      <option value="active">active</option>
                      <option value="inactive">inactive</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Phone Number</FormLabel>
                    <Input
                      type="tel"
                      value={editForm.phone_number}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, phone_number: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Upload Profile Image</FormLabel>
                    <HStack
                      p={2}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="12px"
                      justify="space-between"
                    >
                      <Text fontSize="sm" color="gray.600" noOfLines={1}>
                        {editProfileImageFile?.name || "No new file selected"}
                      </Text>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => editFileInputRef.current?.click()}
                      >
                        Choose file
                      </Button>
                    </HStack>
                    <Input
                      ref={editFileInputRef}
                      type="file"
                      accept="image/*"
                      display="none"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setEditProfileImageFile(file);
                        setEditProfilePreviewUrl(URL.createObjectURL(file));
                      }}
                    />
                    {editProfilePreviewUrl ? (
                      <Box mt={2}>
                        <img
                          src={editProfilePreviewUrl}
                          alt="Edit profile preview"
                          style={{
                            width: "56px",
                            height: "56px",
                            borderRadius: "8px",
                            objectFit: "cover",
                            border: "1px solid #E2E8F0",
                          }}
                        />
                      </Box>
                    ) : null}
                  </FormControl>
                </SimpleGrid>
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onEditClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="edit-person-form"
                bg={VMS_COLORS.primary}
                color="white"
                _hover={{ bg: VMS_COLORS.primaryDark }}
                isLoading={isLoading}
              >
                Save Changes
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Modal isOpen={confirmState.isOpen} onClose={closeConfirm} isCentered>
          <ModalOverlay />
          <ModalContent borderRadius="14px">
            <ModalHeader>{confirmState.title}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text color="gray.700">{confirmState.message}</Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={closeConfirm}>
                Cancel
              </Button>
              <Button
                onClick={runConfirmedAction}
                bg={VMS_COLORS.primary}
                color="white"
                _hover={{ bg: VMS_COLORS.primaryDark }}
                isLoading={isActionLoading}
              >
                Confirm
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </VmsPageCard>
  );
};

export default VmsPeopleUsers;
